(() => {
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const config = () => window.GooeyDuck || {};
  const experienceState = () => window.GooeyExperience?.state?.get?.() || null;
  const continuity = () => window.GooeyContinuity || null;
  const continuityMemory = () => continuity()?.get?.() || {
    recentProducts: [],
    familyCounts: {},
    recentSearches: [],
    recentCollections: [],
    cart: {},
    rewards: {}
  };

  let refreshQueued = false;

  const enabled = () => (
    config().experience?.enabled !== false &&
    config().experience?.gooeyCommentary !== false &&
    !document.body?.classList.contains('experience-gooey-off')
  );

  const hasRescueContext = snapshot => Boolean(
    snapshot?.currentRoom ||
    snapshot?.currentSituation ||
    snapshot?.currentProblem ||
    snapshot?.continuation?.room ||
    snapshot?.continuation?.situation ||
    snapshot?.scentMatch?.primary
  );

  const assetFor = pose => config().mascotAssets?.[pose] || config().mascotAssets?.neutral || '';

  const create = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  };

  const createFigure = (pose, modifier = '') => {
    const figure = create('figure', `gooey-layer3-figure${modifier ? ` ${modifier}` : ''}`);
    figure.dataset.gooeyPose = pose;
    figure.setAttribute('aria-hidden', 'true');
    const image = document.createElement('img');
    image.src = assetFor(pose);
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    figure.appendChild(image);
    return figure;
  };

  const createSpeech = (line, modifier = '') => {
    const bubble = create('div', `gooey-layer3-speech${modifier ? ` ${modifier}` : ''}`);
    bubble.append(
      create('span', 'gooey-layer3-speech__label', 'GOOEY'),
      create('p', 'gooey-layer3-speech__copy', line)
    );
    const scribble = create('span', 'gooey-layer3-speech__scribble');
    scribble.setAttribute('aria-hidden', 'true');
    bubble.append(scribble);
    return bubble;
  };

  const pageReaction = (key, pose, line, modifier = '') => {
    const moment = create('div', `gooey-layer3-page-reaction${modifier ? ` ${modifier}` : ''}`);
    moment.dataset.gooeyLayer3Step3 = 'true';
    moment.dataset.gooeyReactionKey = key;
    moment.append(
      createFigure(pose, 'gooey-layer3-page-reaction__figure'),
      createSpeech(line, 'gooey-layer3-page-reaction__speech')
    );
    return moment;
  };

  const replaceReaction = (existing, key, pose, line, modifier = '') => {
    if (existing?.dataset.gooeyReactionKey === key) return existing;
    const next = pageReaction(key, pose, line, modifier);
    if (existing) existing.replaceWith(next);
    return next;
  };

  const integerFrom = value => {
    const match = String(value || '').replace(/,/g, '').match(/-?\d+/);
    return match ? Number(match[0]) : 0;
  };

  const safeLower = value => String(value || '').trim().toLowerCase();
  const recentEnough = (at, minutes = 12) => Boolean(at && (Date.now() - Number(at)) < minutes * 60 * 1000);
  const familyLabel = family => continuity()?.familyLabel?.(family) || family || 'that';
  const classifyScent = value => continuity()?.classifyScent?.(value) || '';
  const dominantFamily = memory => continuity()?.dominantFamily?.(memory) || null;
  const viewsForHandle = (memory, handle) => (memory.recentProducts || []).filter(product => product.handle === handle).length;

  const stablePick = (lines, seed = '') => {
    const options = (lines || []).filter(Boolean);
    if (!options.length) return '';
    const source = `${seed}|${window.location.pathname}`;
    let hash = 0;
    for (let index = 0; index < source.length; index += 1) hash = ((hash << 5) - hash) + source.charCodeAt(index);
    return options[Math.abs(hash) % options.length];
  };

  const productCartQuip = (product, action, seed) => {
    const handle = safeLower(product?.handle);
    const title = product?.title || 'That candle';
    const quips = {
      'candles-big-wick-energy': {
        added: [
          `${title} is in. Subtle was never really your thing.`,
          `${title} made the cart. Big choice. Obviously.`
        ],
        removed: [
          `${title} got cut. That's one way to reduce the tension.`,
          `${title} is out. Suddenly the cart feels underqualified.`
        ]
      },
      'candles-morning-wood': {
        added: [
          `${title} is in. Glad we handled that.`,
          `${title} made the cut. That's enough maturity for one day.`
        ],
        removed: [
          `You removed ${title}. Cold shower?`,
          `${title} is out. Awkward morning.`
        ]
      },
      'candles-low-tide-high-standards': {
        added: [
          `${title} is in. At least one of those standards held.`,
          `${title} made it. Standards: selectively enforced.`
        ],
        removed: [
          `${title} got the boot. Standards got higher.`,
          `${title} is out. Apparently the standards were not negotiable.`
        ]
      },
      'candles-shuck-around-find-out': {
        added: [
          `${title} is in. You've chosen consequences.`,
          `${title} made the cart. Time to find out.`
        ],
        removed: [
          `${title} got uninvited. Coward.`,
          `${title} is out. You shucked around. You did not find out.`
        ]
      },
      'candles-salty-little-secret': {
        added: [
          `${title} is in. I'll keep my mouth shut.`,
          `${title} made the cart. Your secret is safe-ish.`
        ],
        removed: [
          `${title} got ghosted. Somehow appropriate.`,
          `${title} is out. Secret relationship ended quickly.`
        ]
      },
      'candles-blowin-smoke': {
        added: [
          `${title} is in. Plausible deniability ends here.`,
          `${title} made the cart. The story is already getting better.`
        ],
        removed: [
          `${title} is out. Guess the story didn't hold up.`,
          `${title} got cut. All smoke, no commitment.`
        ]
      }
    };
    return stablePick(quips[handle]?.[action] || [], `${action}:${handle}:${seed}`);
  };

  const stableCardIndex = count => {
    if (count <= 1) return 0;
    const source = window.location.pathname;
    let hash = 0;
    for (let index = 0; index < source.length; index += 1) hash = ((hash << 5) - hash) + source.charCodeAt(index);
    return Math.abs(hash) % count;
  };

  const cardFamily = card => classifyScent(`${card?.querySelector('.product-card__title')?.textContent || ''} ${card?.querySelector('.product-card__notes')?.textContent || ''}`);

  const collectionState = (resultCount, filterCount, sorted, memory) => {
    if (filterCount > 0 && resultCount === 0) {
      return { pose: 'sideEye', line: 'You filtered everything out. Honestly, kind of impressive.' };
    }
    if (filterCount > 0 && resultCount === 1) {
      return { pose: 'impressed', line: 'One left. You have aggressively narrowed the field.' };
    }
    if (filterCount > 0) {
      return { pose: 'approved', line: `${resultCount} left. Look at you pretending this was a controlled process.` };
    }
    if (sorted) {
      return { pose: 'sideEye', line: 'Rearranged the evidence. Surely that fixes everything.' };
    }

    const latestSearch = memory.recentSearches?.[0];
    if (latestSearch?.count === 0 && recentEnough(latestSearch.at, 10)) {
      return { pose: 'concerned', line: 'Search was a bust. Browsing was probably the better move.' };
    }

    const dominant = dominantFamily(memory);
    if (dominant && (memory.recentProducts?.length || 0) >= 3) {
      return { pose: 'impressed', line: `Still circling the ${familyLabel(dominant.family)} stuff. Consistency is technically a virtue.` };
    }
    return null;
  };

  const decorateCollection = snapshot => {
    const page = document.querySelector('.collection-page');
    if (!page) return;
    const memory = continuityMemory();

    const ownSighting = page.querySelector('[data-gooey-layer3-normal-sighting]');
    const rescueSighting = page.querySelector('[data-gooey-layer3-card-sighting]');
    const rescueActive = hasRescueContext(snapshot);

    if (rescueActive || rescueSighting) {
      ownSighting?.remove();
    } else {
      const cards = qsa('.product-card', page);
      const latestViewed = memory.recentProducts?.[0];
      const recentCard = latestViewed && recentEnough(latestViewed.at, 20)
        ? cards.find(card => card.dataset.productHandle === latestViewed.handle)
        : null;
      const dominant = dominantFamily(memory);
      const matching = dominant ? cards.filter(card => cardFamily(card) === dominant.family) : [];
      const hasRecent = Boolean(recentCard);
      const hasPattern = !hasRecent && Boolean(dominant && matching.length);
      const card = recentCard || matching[0] || cards[stableCardIndex(cards.length)];
      const media = card?.querySelector('.product-card__media');

      if (!media) {
        ownSighting?.remove();
      } else {
        const pose = hasRecent ? 'sideEye' : hasPattern ? 'impressed' : 'approved';
        const tagText = hasRecent ? 'STILL THINKING?' : hasPattern ? 'STILL YOUR TYPE' : 'GOOEY APPROVED';
        let sighting = ownSighting;

        if (!sighting) {
          sighting = create('div', 'gooey-layer3-card-sighting');
          sighting.dataset.gooeyLayer3NormalSighting = 'true';
          sighting.dataset.gooeyLayer3Step3 = 'true';
          sighting.append(
            createFigure(pose, 'gooey-layer3-card-sighting__figure'),
            create('span', 'gooey-layer3-card-sighting__tag', tagText)
          );
        }

        if (sighting.parentElement !== media) media.appendChild(sighting);
        sighting.dataset.gooeySightingHandle = card.dataset.productHandle || '';

        const figure = sighting.querySelector('.gooey-layer3-card-sighting__figure');
        const image = figure?.querySelector('img');
        const tag = sighting.querySelector('.gooey-layer3-card-sighting__tag');
        if (figure) figure.dataset.gooeyPose = pose;
        if (image) image.src = assetFor(pose);
        if (tag) tag.textContent = tagText;
      }
    }

    const toolbar = page.querySelector('.collection-toolbar');
    const existing = page.querySelector('[data-gooey-collection-reaction]');
    if (!toolbar) {
      existing?.remove();
      return;
    }

    const url = new URL(window.location.href);
    const filterCount = [...url.searchParams.keys()].filter(key => key.startsWith('filter.')).length;
    const sorted = url.searchParams.has('sort_by');
    const resultCount = integerFrom(toolbar.querySelector('strong')?.textContent);
    const state = collectionState(resultCount, filterCount, sorted, memory);

    if (!state) {
      existing?.remove();
      return;
    }

    const dominant = dominantFamily(memory);
    const key = `collection:${resultCount}:${filterCount}:${sorted}:${dominant?.family || ''}:${memory.recentSearches?.[0]?.count ?? ''}`;
    const reaction = replaceReaction(existing, key, state.pose, state.line, 'gooey-layer3-page-reaction--collection');
    reaction.dataset.gooeyCollectionReaction = 'true';
    if (!existing) toolbar.insertAdjacentElement('afterend', reaction);
  };

  const searchState = (count, term, memory) => {
    const normalized = safeLower(term);
    const repeats = (memory.recentSearches || []).filter(search => safeLower(search.term) === normalized).length;
    const dominant = dominantFamily(memory);

    if (repeats >= 2) {
      return { pose: 'sideEye', line: `You searched “${term}” again. I admire the commitment to the bit.` };
    }
    if (count === 0 && dominant) {
      return { pose: 'concerned', line: `Nothing for “${term}.” You were doing better with the ${familyLabel(dominant.family)} stuff.` };
    }
    if (count === 0) return { pose: 'sideEye', line: `Nothing for “${term}.” That's impressively specific.` };
    if ((memory.recentProducts?.length || 0) >= 3) {
      return { pose: 'impressed', line: `After ${memory.recentProducts.length} candle looks, now we're searching. Fine. ${count} results.` };
    }
    if (count === 1) return { pose: 'impressed', line: 'One result. Apparently you knew exactly what you were doing.' };
    if (count <= 5) return { pose: 'pointing', line: `${count} results. Manageable. Try not to overthink it.` };
    return { pose: 'drinking', line: `${count} results. Great. Now you have a choosing problem.` };
  };

  const decorateSearch = () => {
    const page = document.querySelector('.search-page');
    if (!page) return;
    const meta = page.querySelector('.search-meta');
    const existing = page.querySelector('[data-gooey-search-reaction]');
    if (!meta) {
      existing?.remove();
      return;
    }

    const memory = continuityMemory();
    const count = integerFrom(meta.querySelector('strong')?.textContent);
    const term = page.querySelector('input[name="q"]')?.value?.trim() || 'that';
    const state = searchState(count, term, memory);
    const repeats = (memory.recentSearches || []).filter(search => safeLower(search.term) === safeLower(term)).length;
    const key = `search:${count}:${term}:${repeats}:${memory.recentProducts?.length || 0}`;
    const reaction = replaceReaction(existing, key, state.pose, state.line, 'gooey-layer3-page-reaction--search');
    reaction.dataset.gooeySearchReaction = 'true';
    if (!existing) meta.insertAdjacentElement('afterend', reaction);
  };

  const cartState = (count, memory) => {
    const cart = memory.cart || {};
    const product = cart.lastProduct;
    const actionIsFresh = recentEnough(cart.at, 10);
    const dominant = dominantFamily(memory);
    const actionSeed = `${cart.lastAction || ''}:${cart.at || ''}:${product?.handle || ''}:${count}`;

    if (actionIsFresh && cart.lastAction === 'added' && product) {
      const views = viewsForHandle(memory, product.handle);
      if (views >= 2) {
        return {
          pose: 'celebrating',
          line: stablePick([
            `There it is. You looked at ${product.title} ${views} times. We both knew this was happening.`,
            `${views} looks later, ${product.title} finally made it in. Took you long enough.`,
            `${product.title} survived ${views} rounds of overthinking. Proud of both of you.`,
            `After ${views} visits, ${product.title} got the yes. This relationship has been exhausting.`,
            `${product.title}: viewed ${views} times, added once, emotionally discussed far more.`
          ], `views:${actionSeed}:${views}`)
        };
      }
      if (dominant && product.family === dominant.family) {
        return {
          pose: 'approved',
          line: stablePick([
            `Another ${familyLabel(dominant.family)} one. At least you're consistent.`,
            `More ${familyLabel(dominant.family)}. You absolutely have a type.`,
            `Another ${familyLabel(dominant.family)} scent. The evidence is getting hard to ignore.`,
            `You picked ${familyLabel(dominant.family)} again. We're past coincidence.`
          ], `family:${actionSeed}:${dominant.family}`)
        };
      }
      if (memory.rewards?.joinedThisSession) {
        return {
          pose: 'celebrating',
          line: stablePick([
            `Rewards on. ${product.title} in the cart. Look at this sudden competence.`,
            `${product.title} is in and earning points. Disturbingly efficient.`,
            `Points active. ${product.title} added. Somebody read the assignment.`,
            `${product.title} made the cart and the points meter noticed. Very adult of you.`
          ], `rewards-added:${actionSeed}`)
        };
      }
      return {
        pose: 'approved',
        line: productCartQuip(product, 'added', actionSeed) || stablePick([
          `Good. ${product.title} made the cut.`,
          `${product.title} is in. Apparently we're doing this.`,
          `${product.title} just moved into the cart. Try to act surprised.`,
          `Added ${product.title}. Your judgment is improving in very specific areas.`,
          `${product.title} made it past the talking stage.`,
          `Well, ${product.title} is coming home with you unless you panic again.`
        ], `added:${actionSeed}`)
      };
    }

    if (actionIsFresh && cart.lastAction === 'removed' && product) {
      return {
        pose: 'sideEye',
        line: productCartQuip(product, 'removed', actionSeed) || stablePick([
          `You took ${product.title} back out. Commitment is hard.`,
          `${product.title} got dumped. No closure. Brutal.`,
          `Well, ${product.title} just got ghosted.`,
          `${product.title} is out. I'll tell it you said, “It's not you, it's me.”`,
          `Removed ${product.title}. The cart has trust issues now.`,
          `${product.title} didn't survive the second-guessing.`,
          `You changed your mind on ${product.title}. I'm absolutely judging this quietly.`,
          `${product.title} is gone. This relationship moved fast.`
        ], `removed:${actionSeed}`)
      };
    }

    if (memory.rewards?.joinedThisSession && count > 0) {
      return {
        pose: 'approved',
        line: stablePick([
          'At least those candles are earning points now.',
          'The cart has points attached now. Suddenly this looks almost responsible.',
          'Every candle in there is earning points. Convenient little justification.',
          'Points are on. Keep calling this financial planning if it helps.'
        ], `rewards-cart:${count}:${memory.rewards?.joinedAt || ''}:${cart.at || ''}`)
      };
    }

    if (count <= 0) {
      return {
        pose: 'sideEye',
        line: stablePick([
          'Nothing in here. Bold strategy.',
          'Empty cart. Very responsible. Deeply suspicious.',
          'Nothing. Not even one bad decision.',
          'The cart is empty. I’m sure this restraint will last.',
          'Wow. Zero candles. Who are you and what have you done?',
          'Empty. We can pretend this was the plan.'
        ], `empty:${cart.at || ''}`)
      };
    }
    if (count === 1) {
      return {
        pose: 'neutral',
        line: stablePick([
          'One candle. Suspiciously responsible.',
          "One candle. That's technically restraint.",
          'Just one? I expected less self-control.',
          'One candle. Very mature. Hate that for us.',
          'One candle. A starter bad decision.'
        ], `one:${cart.at || ''}:${cart.lastProduct?.handle || ''}`)
      };
    }
    if (count === 2) {
      return {
        pose: 'approved',
        line: stablePick([
          "Two candles. Okay, now we're shopping.",
          'Two candles. Now it looks intentional.',
          "Two. That's not a problem. That's a pairing.",
          'Two candles. One for the room, one for the alibi.',
          'Two. This is how collections start.'
        ], `two:${cart.at || ''}:${cart.lastProduct?.handle || ''}`)
      };
    }
    if (count === 3) {
      return {
        pose: 'impressed',
        line: stablePick([
          'Three candles. This is becoming a pattern.',
          "Three candles. We have crossed into 'thing' territory.",
          'Three. Enough to call it a haul.',
          'Three candles. The cart has a personality now.',
          'Three. Nobody accidentally buys three candles.'
        ], `three:${cart.at || ''}:${cart.lastProduct?.handle || ''}`)
      };
    }
    return {
      pose: 'drinking',
      line: stablePick([
        `${count} candles. Restraint has officially left the building.`,
        `${count} candles. Okay, this got serious fast.`,
        `${count} candles. At this point, the cart needs its own ZIP code.`,
        `${count} candles. I support whatever excuse we're using.`,
        `${count} candles. That's not browsing anymore.`,
        `${count} candles. You came for one. Sure.`,
        `${count} candles. We stopped pretending a while ago.`
      ], `many:${count}:${cart.at || ''}:${cart.lastProduct?.handle || ''}`)
    };
  };

  const cartCount = () => integerFrom(document.querySelector('[data-cart-count]')?.textContent);

  const decorateCartDrawer = snapshot => {
    const drawer = document.querySelector('[data-cart-drawer]');
    const host = document.querySelector('#CartDrawerContent');
    if (!drawer || !host) return;

    const existing = host.querySelector('[data-gooey-normal-cart-reaction]');
    if (hasRescueContext(snapshot) || drawer.getAttribute('aria-hidden') !== 'false') {
      existing?.remove();
      return;
    }

    const memory = continuityMemory();
    const count = cartCount();
    const state = cartState(count, memory);
    const key = `drawer-cart:${count}:${memory.cart?.lastAction || ''}:${memory.cart?.lastProduct?.handle || ''}:${memory.cart?.at || ''}:${Boolean(memory.rewards?.joinedThisSession)}`;
    if (existing?.dataset.gooeyReactionKey === key) return;

    const moment = create('section', 'gooey-layer3-cart-moment gooey-layer3-cart-moment--normal');
    moment.dataset.gooeyLayer3Step3 = 'true';
    moment.dataset.gooeyNormalCartReaction = 'true';
    moment.dataset.gooeyReactionKey = key;
    moment.append(
      createFigure(state.pose, 'gooey-layer3-cart-moment__figure'),
      createSpeech(state.line, 'gooey-layer3-cart-moment__speech')
    );
    if (existing) existing.replaceWith(moment);
    else host.prepend(moment);
  };

  const decorateCartPage = snapshot => {
    const page = document.querySelector('.cart-page');
    if (!page) return;
    const head = page.querySelector('.cart-page__head');
    const existing = page.querySelector('[data-gooey-cart-page-reaction]');
    if (!head) return;

    const quantities = qsa('.cart-line input[name="updates[]"]', page);
    const count = quantities.length
      ? quantities.reduce((sum, input) => sum + Math.max(0, integerFrom(input.value)), 0)
      : 0;
    const memory = continuityMemory();
    let state = cartState(count, memory);
    if (hasRescueContext(snapshot) && count > 0) {
      state = {
        pose: 'approved',
        line: stablePick([
          'Approved. Try not to make this weird.',
          'Room fixed. Cart loaded. I have no notes. That is a lie.',
          'That should handle the room. Your judgment remains under review.',
          'Good choice. Weird route getting here, though.'
        ], `rescue-cart:${count}:${memory.cart?.at || ''}`)
      };
    }
    const key = `cart-page:${count}:${hasRescueContext(snapshot)}:${memory.cart?.lastAction || ''}:${memory.cart?.lastProduct?.handle || ''}:${memory.cart?.at || ''}`;
    const reaction = replaceReaction(existing, key, state.pose, state.line, 'gooey-layer3-page-reaction--cart');
    reaction.dataset.gooeyCartPageReaction = 'true';
    if (!existing) head.insertAdjacentElement('afterend', reaction);
  };

  const rewardsState = (root, memory) => {
    const status = root.querySelector('[data-gdr-status]');
    const statusText = status?.textContent?.trim() || '';
    const success = status?.dataset.tone === 'success' || /welcome to gooey duck rewards/i.test(statusText);
    if (success) return { key: `rewards:success:${statusText}`, pose: 'celebrating', line: 'There. Now your bad decisions have a points balance.' };

    const title = root.querySelector('[data-gdr-page-title]')?.textContent?.trim() || '';
    const primary = root.querySelector('[data-gdr-page-primary]');
    const joined = /^you['’]ve got\s+\d+/i.test(title) || /view my rewards/i.test(primary?.textContent || '');
    const count = Number(memory.cart?.count || 0);

    if (joined && memory.rewards?.joinedThisSession) {
      return { key: `rewards:new-member:${title}:${count}`, pose: 'celebrating', line: count > 0 ? 'Rewards are on. Good. Now the cart can start earning its keep.' : "You're in. Now go make those points somebody else's problem." };
    }
    if (joined && count > 0) {
      return { key: `rewards:member-cart:${title}:${count}`, pose: 'approved', line: `Points are ready. So ${count === 1 ? 'is that candle' : `are those ${count} candles`} in your cart.` };
    }
    if (joined) return { key: `rewards:member:${title}`, pose: 'approved', line: "Points, perks, and consequences. You're officially in." };

    const readyToJoin = Boolean(root.querySelector('[data-gdr-action="join"]'));
    if (readyToJoin && count > 0) {
      return { key: `rewards:join-cart:${count}`, pose: 'pointing', line: `You've already got ${count === 1 ? 'a candle' : `${count} candles`} waiting. Might as well make ${count === 1 ? 'it' : 'them'} earn points.` };
    }
    if (readyToJoin && (memory.recentProducts?.length || 0) >= 3) {
      return { key: `rewards:join-browsed:${memory.recentProducts.length}`, pose: 'impressed', line: `You've looked at ${memory.recentProducts.length} candles. At this point, take the free points.` };
    }
    if (readyToJoin) return { key: 'rewards:join-ready', pose: 'impressed', line: "One button and you get points. I've made harder decisions before breakfast." };

    if (count > 0) return { key: `rewards:signed-out-cart:${count}`, pose: 'peek', line: 'There are candles in the cart and free points over here. Just saying.' };
    return { key: 'rewards:signed-out', pose: 'peek', line: 'Free points for joining. Finally, math I support.' };
  };

  const decorateRewards = () => {
    const root = document.querySelector('[data-gdr-widget="page"]');
    if (!root) return;
    const hero = root.querySelector('[data-gdr-page-section="hero"]');
    if (!hero) return;

    const memory = continuityMemory();
    const state = rewardsState(root, memory);
    const existing = root.querySelector('[data-gooey-rewards-reaction]');
    const reaction = replaceReaction(existing, state.key, state.pose, state.line, 'gooey-layer3-page-reaction--rewards');
    reaction.dataset.gooeyRewardsReaction = 'true';
    if (!existing) hero.insertAdjacentElement('afterend', reaction);
  };

  const cleanup = () => {
    qsa('[data-gooey-layer3-step3]').forEach(node => node.remove());
  };

  const refresh = () => {
    if (refreshQueued) return;
    refreshQueued = true;
    window.requestAnimationFrame(() => {
      refreshQueued = false;
      if (!enabled()) {
        cleanup();
        return;
      }
      const snapshot = experienceState();
      decorateCollection(snapshot);
      decorateSearch();
      decorateCartDrawer(snapshot);
      decorateCartPage(snapshot);
      decorateRewards();
    });
  };

  const observer = new MutationObserver(refresh);
  const start = () => {
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-hidden', 'data-tone', 'hidden']
    });
    refresh();
  };

  document.addEventListener('gooey:experience:statechange', refresh);
  document.addEventListener('gooey:continuity:change', refresh);
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('change', event => {
    if (event.target instanceof Element && event.target.matches('.cart-page input[name="updates[]"]')) refresh();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();